import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { Wishlist } from '@theme/wishlist';

/**
 * A heart-shaped wishlist toggle. Works instantly for guests (localStorage-backed) and
 * signed-in customers (server-backed) alike - this component reads/writes through the
 * shared `Wishlist` manager and doesn't know or care which storage is behind it.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggle
 *
 * @extends {Component<Refs>}
 */
class WishlistButton extends Component {
  requiredRefs = ['toggle'];

  get productId() {
    return this.getAttribute('product-id') ?? '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);

    // Reflects whatever is known right now; if the account wishlist is still loading,
    // this self-corrects the moment the wishlist:update event fires after it resolves.
    this.#render(Wishlist.has(this.productId));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
  }

  /** @param {Event} event */
  toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!this.productId) return;

    this.#render(Wishlist.toggle(this.productId));
  };

  /** @param {import('@theme/events').WishlistUpdateEvent} event */
  #onWishlistUpdate = (event) => {
    const { changedProductId } = event.detail;
    // A targeted toggle only needs to update the matching button; an untargeted event
    // (initial load, guest -> account merge) means every button should re-check.
    if (changedProductId && changedProductId !== this.productId) return;

    this.#render(Wishlist.has(this.productId));
  };

  /** @param {boolean} active */
  #render(active) {
    const { toggle } = this.refs;
    if (!toggle) return;

    toggle.setAttribute('aria-pressed', String(active));
    toggle.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
